/**
 * Named gesture-tuning presets.
 *
 * Centralising the magic numbers keeps the feel consistent across every gesture
 * surface and makes tuning a one-line change. Each preset bundles the
 * recognizer's `fling`/`thresholdPx` with the consumer-facing layout constants
 * (commit thresholds, rubber-band limits, animation durations) that the
 * recognizer itself does not own.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/** Per-row swipe-to-action (e.g. resolve / advance status on a finding row). */
export const FINDING_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/** Per-row swipe-to-delete (template / site rows): single-direction commit. */
export const ENTITY_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;

/**
 * Inspection-run section carousel: horizontal swipe to page between sections of
 * a checklist (same slide physics as the month calendar carousel). Commits at
 * the smaller of a fixed travel or a fraction of the viewport width, or a fling.
 */
export const RUN_SECTION_CAROUSEL = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 64,
  commitThresholdRatio: 0.22,
  transitionMs: 260,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Swipe-to-dismiss for a transient toast: horizontal drag in either direction
 * dismisses past the commit threshold or on a fling. Rubber-bands toward the
 * non-actionable edge is unnecessary (both directions commit), so the toast
 * simply follows the finger and snaps back if the release falls short.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  hapticMs: 12,
  commitAnimationMs: 200,
  cancelAnimationMs: 200,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;
